<template>
    <customer-dashboard-layout id="cdb-index-page" class="h-100">

        <section id="cdb-index-navigation">
            <div class="row d-flex align-items-end">
                <div class="col-7 col-md-6">
                    <h1 class="mb-0" v-if="hasMultipleEntries">{{$t('cdb.subscriptions.title.plural')}}</h1>
                    <h1 class="mb-0" v-else>{{$t('cdb.subscriptions.title.singular') }}</h1>
                </div>
                <div class="col-5 col-6 d-flex justify-content-end">
                    <b-button :to="$getUrl('cdb-dashboard-profile')" variant="link">
                        {{ $t('cdb.subscriptions.profile') }}
                    </b-button>
                </div>
            </div>
        </section>

        <section id="cdb-index-entries" class="mt-3" v-if="!isLoading">
            <alert-box type="warning" class="mb-3 mt-1" v-if="isDriverStatusInactive">
                Your account status is <strong>Inactive</strong>. You cannot subscribe to any car at this moment.
            </alert-box>
            <div v-if="entries.length > 3">
                <subscription-entry
                    v-for="entry in entries.slice(0, 3)"
                    :key="entry.order_number"
                    :has-multiple-entries="hasMultipleEntries"
                    :entry-data="entry"
                    :isLoading="isLoading"
                ></subscription-entry>
            </div>
            <div v-else-if="entries.length > 0">
                <subscription-entry
                    v-for="entry in entries"
                    :key="entry.order_number"
                    :has-multiple-entries="hasMultipleEntries"
                    :entry-data="entry"
                    :isLoading="isLoading"
                ></subscription-entry>
            </div>
            <div v-else>
                {{$t('cdb.subscriptions.no_subscriptions') }}
            </div>

            <div>
                <div v-if="displayMoreSubscriptions">
                    <subscription-entry
                        v-for="entry in entries.slice(3, entries.length)"
                        :key="entry.order_number"
                        :has-multiple-entries="hasMultipleEntries"
                        :entry-data="entry"
                        :isLoading="isLoading"
                    ></subscription-entry>
                </div>
                <div v-if="entries.length > 1">
                    <div v-if="!toggled">
                        <button @click="showMoreSubscriptions">
                            {{$t('cdb.subscriptions.show_more')}}
                        </button>
                    </div>
                    <div v-else>
                        <button @click="showMoreSubscriptions">
                            {{$t('cdb.subscriptions.show_less')}}
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <section class="mt-3" v-else>
            <div class="list-group-item">
                <div class="row my-1">
                    <div class="col-12 col-md-4">
                        <b-skeleton-img></b-skeleton-img>
                    </div>
                    <div class="col-12 col-8 d-flex flex-column">
                        <div class="row">
                            <div class="col-12 pt-3 pb-4">
                                <b-skeleton height="2.5rem"></b-skeleton>
                            </div>
                        </div>
                        <div class="row mt-auto">
                            <div class="col-12 col-md-6 my-3 my-md-0">
                                <b-skeleton height="2rem"></b-skeleton>
                            </div>
                            <div class="col-12 col-md-6 d-flex align-items-end justify-content-end">
                                <b-skeleton height="2rem" width="80%"></b-skeleton>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mt-3">
                    <div class="col-12 d-flex">
                        <b-skeleton width="50px" height="1.5rem" class="mr-2"></b-skeleton>
                        <b-skeleton width="80px" height="1.5rem" class="mr-2"></b-skeleton>
                        <b-skeleton width="80px" height="1.5rem" class="mr-2"></b-skeleton>
                    </div>
                </div>
            </div>
            <div class="list-group-item">
                <div class="row my-1">
                    <div class="col-12 col-md-4" >
                        <b-skeleton-img></b-skeleton-img>

                        <b-skeleton width="80%" height="2rem" class="mt-2"></b-skeleton>
                        <b-skeleton width="70%"></b-skeleton>
                        <b-skeleton width="73%"></b-skeleton>

                    </div>
                    <div class="col-12 col-md-8 d-flex flex-column">
                        <div class="row">
                            <div class="col-12 pt-3 pb-4">
                                <b-skeleton height="2.5rem"></b-skeleton>

                                <div class="d-flex flex-column">
                                    <b-skeleton width="80%" height="2rem" class="mt-5"></b-skeleton>
                                    <b-skeleton width="70%" class="mt-2"></b-skeleton>
                                    <b-skeleton width="73%" class="mt-2"></b-skeleton>
                                    <b-skeleton width="68%" class="mt-2"></b-skeleton>
                                    <b-skeleton width="73%" class="mt-2"></b-skeleton>
                                    <b-skeleton width="80%" class="mt-2"></b-skeleton>
                                    <b-skeleton width="55%" class="mt-2"></b-skeleton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section id="cdb-modals">
            <modal-add-ssn></modal-add-ssn>
            <modal-vehicle-switch></modal-vehicle-switch>
            <modal-help-and-contact></modal-help-and-contact>
            <modal-change-duration></modal-change-duration>
            <modal-change-kilometer-package></modal-change-kilometer-package>
            <modal-change-payment-methods v-if="!isLoading && !subsidiaryHasOwnPaymentHandling"></modal-change-payment-methods>
            <modal-change-address></modal-change-address>
            <modal-cancel-subscription></modal-cancel-subscription>
            <modal-upload-document></modal-upload-document>
            <modal-change-return-method></modal-change-return-method>
            <modal-change-return-date></modal-change-return-date>
        </section>
    </customer-dashboard-layout>
</template>

<script>
import MainLayout from '../../layouts/CustomerDashboardLayout.vue'
import SubscriptionEntry from "../../components/organisms/SubscriptionEntry.vue";
import CustomerDashboardLayout from "../../layouts/CustomerDashboardLayout.vue";
import ModalAddSsn from "../../components/molecules/ModalAddSsn.vue";
import ModalHelpAndContact from "../../components/molecules/ModalHelpAndContact.vue";
import ModalChangeDuration from "../../components/molecules/ModalChangeDuration.vue";
import ModalChangeKilometerPackage from "../../components/molecules/ModalChangeKilometerPackage.vue";
import ModalChangeAddress from "../../components/molecules/ModalChangeAddress.vue";
import ModalChangePaymentMethods from "../../components/molecules/ModalChangePaymentMethods.vue";
import ModalCancelSubscription from "../../components/molecules/ModalCancelSubscription.vue"
import ModalChangeReturnMethod from "../../components/molecules/ModalChangeReturnMethod.vue"
import ModalChangeReturnDate from "../../components/molecules/ModalChangeReturnDate.vue"
import ModalVehicleSwitch from "../../components/molecules/ModalVehicleSwitch.vue"
import { EventBus } from "../../event-bus";
import { BSkeleton, BSkeletonImg, BButton } from "bootstrap-vue"
import AlertBox from "../../components/atoms/AlertBox.vue";
import ModalUploadDocument from "../../components/molecules/ModalUploadDocument.vue";

export default {
    components: {
        ModalUploadDocument,
        ModalChangePaymentMethods,
        ModalChangeAddress,
        ModalChangeKilometerPackage,
        ModalChangeDuration,
        ModalVehicleSwitch,
        ModalAddSsn,
        ModalHelpAndContact,
        ModalCancelSubscription,
        ModalChangeReturnMethod,
        ModalChangeReturnDate,
        CustomerDashboardLayout,
        SubscriptionEntry,
        BButton,
        MainLayout,
        BSkeleton, BSkeletonImg,
        AlertBox
    },
    data() {
        return {
            entries: this.getEntries,
            isLoading: true,
            displayMoreSubscriptions: false,
            toggled: false
        }
    },
    methods: {
        getEntries() {
            this.isLoading = true

            this.$axios.post('api/v1/dashboard/list_subscriptions?active=true', {
            }).then(response => {
                if(response.data && response.data.length > 0) {
                    this.entries = response.data.reverse()
                }
                
                this.isLoading = false
            })
            .catch(error => {
                this.error = error.response.data.message
            });
        },
        showMoreSubscriptions() {
            this.toggled = !this.toggled
            this.displayMoreSubscriptions = !this.displayMoreSubscriptions
        }
    },
    mounted() {
        this.getEntries();
        EventBus.$on('cdb-reload-subscriptions', this.getEntries);
    },
    computed: {
        hasMultipleEntries: function () {
            return this.entries.length !== 1
        },
        isDriverStatusInactive() {
            return this.$root.user?.state?.data?.driver_status === 'INACTIVE'
        },
        subsidiaryHasOwnPaymentHandling() {
            if (this.entries.length === 0) {
                return false;
            } else {
                return this.entries[0].subsidiary_has_own_payment_handling === 1 ?? false;
            }
        }
    },
    head: {
        title: function () {
            return {
                inner: this.$root.whitelabel.state.settings.meta_title,
                complement: this.$t('meta.cdb.title')
            }
        },
        meta: function () {
            return [
                { name: 'description', content: this.$t('meta.cdb.description') }
            ]
        }
    }
}
</script>
